import { KeyEnum } from 'app/hooks/useKeyBinder';
import { container } from 'aws-amplify';
import {
  ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { scrollToChildElement } from 'utils';

export default function Options<T>({
  optionItems,
  onSelect,
  children,
  itemId = 'id' as any,
  value,
  canSelect,
  className = 'max-h-full overflow-y-auto',
  disableItemStyle = false,
}: {
  optionItems: T[];
  canSelect?: boolean;
  value?: T;
  itemId?: keyof T;
  onSelect: (selected: T) => void;
  children: (item: T, isActive: boolean) => ReactNode;
  disableItemStyle?: boolean;
  className?: string;
}) {
  const [cursor, setCursor] = useState(0);

  useEffect(() => {
    return () => {};
  }, []);
  useEffect(() => {
    const index = value
      ? optionItems.findIndex((_) => _[itemId] == value[itemId])
      : 0;

    setCursor((_) => (index > -1 ? index : 0));
  }, [value, optionItems]);

  const cursorIndexRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const moveCursorTo = useCallback(
    (position: number) => setCursor((state) => position),
    [],
  );

  const moveCursorUp = useCallback(
    () => setCursor((state) => (state > 0 ? state - 1 : state)),
    [],
  );

  const moveCusrorDown = useCallback(
    () => setCursor((s) => (s < optionItems.length - 1 ? s + 1 : s)),
    [optionItems],
  );

  useLayoutEffect(() => {
    if (containerRef.current && cursorIndexRef.current) {
      scrollToChildElement(containerRef.current, cursorIndexRef.current);
    }
  }, [containerRef.current, cursorIndexRef.current]);

  const onEnterSelect = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === KeyEnum.ENTER && optionItems[cursor]) {
        e.stopPropagation();
        e.preventDefault();
        onSelect(optionItems[cursor]);
      }
    },
    [cursor, optionItems, canSelect],
  );

  const cursorNavigationListener = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === KeyEnum.ARROW_DOWN) {
        e.stopPropagation();
        e.preventDefault();
        moveCusrorDown();
      }

      if (e.key === KeyEnum.ARROW_UP) {
        e.stopPropagation();
        e.preventDefault();
        moveCursorUp();
      }
    },
    [moveCusrorDown, moveCursorUp],
  );

  useEffect(() => {
    window.addEventListener('keydown', onEnterSelect);

    return () => window.removeEventListener('keydown', onEnterSelect);
  }, [onEnterSelect]);

  useEffect(() => {
    window.addEventListener('keydown', cursorNavigationListener);

    return () =>
      window.removeEventListener('keydown', cursorNavigationListener);
  }, [moveCusrorDown, moveCursorUp]);

  return (
    <div
      ref={containerRef}
      onKeyDown={(e) => {
        if (e.key === KeyEnum.ENTER && optionItems[cursor]) {
          e.stopPropagation();
          e.preventDefault();
          onSelect(optionItems[cursor]);
        }
      }}
      className={`text-sm max-h-80 overflow-y-auto`}
    >
      {optionItems.length == 0 && (
        <div className="text-sm text-gray-700 pl-3 py-2">No Results Found</div>
      )}
      {optionItems.length > 0 &&
        optionItems.map((option, index) => (
          <div
            ref={cursor === index ? cursorIndexRef : null}
            key={option[itemId] as unknown as string}
            className={`${
              disableItemStyle ? '' : optionItemStyle(cursor === index)
            }`}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              setCursor(index);
              onSelect(optionItems[index]);
            }}
          >
            {children(option, cursor === index)}
          </div>
        ))}
    </div>
  );
}

export const optionItemStyle = (isActive: boolean) => {
  return isActive
    ? 'bg-green text-white cursor-pointer pl-3 py-2 border-gray-light'
    : 'hover:bg-green-10 text-gray-dark cursor-pointer pl-3 py-2';
};
